import { Box, VStack, Flex, Text, Link as ChakraLink } from '@chakra-ui/react'
import { Link as RouterLink } from 'react-router-dom'
import { ManualScreenshot } from './ManualScreenshot'

export type StepScreenshot = { src: string; alt: string; caption: string }

export type ManualLink = { label: string; to?: string }

export function HelpStepContent({
  children,
  screenshot,
  screenshots,
  border,
  textSecondary,
}: {
  children: React.ReactNode
  screenshot?: StepScreenshot
  screenshots?: StepScreenshot[]
  border: string
  textSecondary: string
}) {
  const shots = screenshots?.length ? screenshots : screenshot ? [screenshot] : []

  if (shots.length === 0) {
    return (
      <VStack align="stretch" spacing={4} maxW="720px">
        {children}
      </VStack>
    )
  }

  return (
    <Flex
      direction={{ base: 'column', lg: 'row' }}
      align="flex-start"
      gap={{ base: 4, lg: 8 }}
      w="full"
    >
      <VStack align="stretch" spacing={4} flex="1" minW={0} maxW={{ lg: '520px' }}>
        {children}
      </VStack>
      <VStack
        align="stretch"
        spacing={4}
        flex={{ base: 'none', lg: '1.15' }}
        w="full"
        minW={{ lg: '340px' }}
        maxW={{ lg: '560px' }}
      >
        {shots.map((shot) => (
          <ManualScreenshot
            key={shot.src}
            src={shot.src}
            alt={shot.alt}
            caption={shot.caption}
            border={border}
            textSecondary={textSecondary}
          />
        ))}
      </VStack>
    </Flex>
  )
}

/** Alias used by User Manual (StepContent parity). */
export const StepContent = HelpStepContent

export function TroubleshootStep({
  symptom,
  causes,
  steps,
  screenshots,
  screenshot,
  manualLinks,
  border,
  textSecondary,
}: {
  symptom: string
  causes: string[]
  steps: string[]
  screenshots?: StepScreenshot[]
  screenshot?: StepScreenshot
  /** User Manual sections to open after fixing (by name). */
  manualLinks?: ManualLink[]
  border: string
  textSecondary: string
}) {
  return (
    <HelpStepContent
      screenshot={screenshot}
      screenshots={screenshots}
      border={border}
      textSecondary={textSecondary}
    >
      <Box>
        <Box as="strong" display="block" mb={2}>
          Symptom
        </Box>
        <Box as="p" mb={4}>
          {symptom}
        </Box>
        <Box as="strong" display="block" mb={2}>
          Likely cause
        </Box>
        <Box as="ul" pl={5} mb={4} style={{ listStyleType: 'disc' }}>
          {causes.map((cause) => (
            <Box as="li" key={cause} mb={1}>
              {cause}
            </Box>
          ))}
        </Box>
        <Box as="strong" display="block" mb={2}>
          How to fix
        </Box>
        <Box as="ol" pl={5} mb={manualLinks?.length ? 4 : 0} style={{ listStyleType: 'decimal' }}>
          {steps.map((step) => (
            <Box as="li" key={step} mb={2}>
              {step}
            </Box>
          ))}
        </Box>
        {manualLinks && manualLinks.length > 0 && (
          <Box>
            <Box as="strong" display="block" mb={2}>
              After fixing — how-to in the User Manual
            </Box>
            <Box as="ul" pl={5} style={{ listStyleType: 'disc' }}>
              {manualLinks.map((link) => (
                <Box as="li" key={link.label} mb={1}>
                  <ChakraLink as={RouterLink} to={link.to || '/user-manual'} color="blue.500">
                    User Manual → {link.label}
                  </ChakraLink>
                </Box>
              ))}
            </Box>
            <Text fontSize="sm" color={textSecondary} mt={2}>
              Open the linked section for field-by-field create/edit guidance.
            </Text>
          </Box>
        )}
      </Box>
    </HelpStepContent>
  )
}
